import React, { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandParameters, CommandType } from '@thingslog/repositories';
import {
  ParameterValueLabels,
  parseParameterName,
  SharedParameterValueLabels,
  translateParameterName,
  translateParameterValue,
} from '../utils/commandLabels';

interface ParameterEntry {
  name: string;
  label: string;
  value: string;
  groupIndex: number | null;
}

const groupParameters = (entries: ParameterEntry[]): [number | null, ParameterEntry[]][] => {
  const groups = new Map<number | null, ParameterEntry[]>();

  entries.forEach((entry: ParameterEntry): void => {
    const bucket = groups.get(entry.groupIndex);
    if (bucket) bucket.push(entry);
    else groups.set(entry.groupIndex, [entry]);
  });

  return [...groups.entries()].sort(
    (a: [number | null, ParameterEntry[]], b: [number | null, ParameterEntry[]]): number =>
      (a[0] ?? -1) - (b[0] ?? -1)
  );
};

const CommandParametersCell: FC<CommandParametersCellProps> = ({
  commandType,
  parameters,
  valueLabels,
  sharedValueLabels,
}: CommandParametersCellProps) => {
  const { t } = useTranslation();

  const entries = useMemo((): ParameterEntry[] => {
    return Object.entries(parameters ?? {}).map(
      ([name, value]: [string, string]): ParameterEntry => ({
        name,
        label: translateParameterName(t, name),
        value: translateParameterValue(valueLabels, commandType, name, value, sharedValueLabels),
        groupIndex: parseParameterName(name).groupIndex,
      })
    );
  }, [parameters, commandType, valueLabels, sharedValueLabels, t]);

  if (entries.length === 0) {
    return <span className="text-sm italic text-slate-400">{t('commands_parameters_empty')}</span>;
  }

  const groups = groupParameters(entries);

  return (
    <div className="flex flex-col gap-1.5 py-1">
      {groups.map(
        ([groupIndex, groupEntries]: [number | null, ParameterEntry[]]): ReactNode => (
          <div key={groupIndex ?? 'ungrouped'} className="flex flex-wrap items-center gap-1.5">
            {groupIndex !== null && (
              <span className="mr-0.5 whitespace-nowrap rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                {t('commands_schedule_group', { index: groupIndex })}
              </span>
            )}
            {groupEntries.map(
              (entry: ParameterEntry): ReactNode => (
                <span
                  key={entry.name}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded border border-slate-200 bg-white px-2 py-1 text-[13px] leading-5"
                  title={`${entry.name} = ${entry.value}`}
                >
                  <span className="text-slate-500">{entry.label}</span>
                  <span className="font-semibold text-slate-800">{entry.value}</span>
                </span>
              )
            )}
          </div>
        )
      )}
    </div>
  );
};

interface CommandParametersCellProps {
  commandType: CommandType;
  parameters: CommandParameters;
  valueLabels: ParameterValueLabels;
  sharedValueLabels: SharedParameterValueLabels;
}

export default CommandParametersCell;
